import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Container, Link } from '@mui/material'
import backgroundImage from './assets/izzi-background.jpg'
import Layout from './components/Layout'
import AdminLayout from './components/AdminLayout'
import TicketViewer from './pages/TicketViewer'
import TicketEmbedView from './pages/TicketEmbedView'
import PreauthView from './pages/PreauthView'
import LoginPage from './pages/LoginPage'
import AccessDeniedPage from './pages/AccessDeniedPage'
import AdminDashboardPage from './pages/AdminDashboardPage'
import AdminAgentsPage from './pages/AdminAgentsPage'
import AdminTestRunsPage from './pages/AdminTestRunsPage'
import AdminEmailManagementPage from './pages/AdminEmailManagementPage'
import AdminMemoryBrowserPage from './pages/AdminMemoryBrowserPage'
import AdminEnvironmentPage from './pages/AdminEnvironmentPage'
import AdminKnowledgePage from './pages/AdminKnowledgePage'
import AdminClusterPage from './pages/AdminClusterPage'
import AdminBackgroundJobsPage from './pages/AdminBackgroundJobsPage'
import AdminUsersPage from './pages/AdminUsersPage'
import AdminModelsPage from './pages/AdminModelsPage'
import AdminProfilingPage from './pages/AdminProfilingPage'
import AdminTicketManagementPage from './pages/AdminTicketManagementPage'
import AdminDataExportPage from './pages/AdminDataExportPage'
import AnalyticsDashboard from './pages/AnalyticsDashboard'
import { useAuth } from './hooks/useAuth'
import { setAccessTokenGetter, setAuthFailureHandler } from './services/api'
import { ScreenSaverProvider } from './contexts/ScreenSaverContext'
import ScreenSaver from './components/ScreenSaver'

// Global error state
window.showAccessDeniedModal = null

function App() {
  const { isAuthenticated, isLoading, getAccessToken, user, logout, handleAuthExpired } = useAuth()
  const [accessDeniedModal, setAccessDeniedModal] = useState({ open: false, message: '' })

  // Set the access token getter + 401 re-auth handler for the API service
  useEffect(() => {
    setAccessTokenGetter(getAccessToken)
    setAuthFailureHandler(handleAuthExpired)
  }, [getAccessToken, handleAuthExpired])

  // Set global function to show access denied modal
  useEffect(() => {
    window.showAccessDeniedModal = (message = 'Access denied. You do not have the required permissions.') => {
      setAccessDeniedModal({ open: true, message })
    }

    return () => {
      window.showAccessDeniedModal = null
    }
  }, [])

  const handleCloseModal = () => {
    setAccessDeniedModal({ open: false, message: '' })
  }

  const handleLogout = async () => {
    setAccessDeniedModal({ open: false, message: '' })
    await logout()
  }

  // Check if user has admin role (from token OR application roles)
  const hasTokenAdminRole = user?.roles?.some(role =>
    role.toLowerCase() === 'agentflow.admin'
  )
  const hasApplicationAdminRole = user?.application_roles?.some(role =>
    role.toLowerCase() === 'application.admin'
  )
  const isAdmin = hasTokenAdminRole || hasApplicationAdminRole

  const location = useLocation()

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          bgcolor: 'background.default',
        }}
      >
        Loading...
      </Box>
    )
  }

  if (!isAuthenticated) {
    return <LoginPage />
  }

  // Preauth is a silent auth iframe — return early before mounting any providers.
  if (location.pathname === '/preauth') {
    return <PreauthView />
  }

  return (
    <ScreenSaverProvider>
      <Routes>
        {/* Embed Routes - NO Layout wrapper (for iframe embedding) */}
        <Route path="/ticketembed/:ticketId" element={<TicketEmbedView />} />
        <Route path="/preauth" element={<PreauthView />} />

        {/* Admin Routes - WITH AdminLayout wrapper */}
        {isAdmin && (
          <Route path="/admin/*" element={
            <AdminLayout>
              <Routes>
                <Route path="/" element={<AdminDashboardPage />} />
                <Route path="/agents" element={<AdminAgentsPage />} />
                <Route path="/users" element={<AdminUsersPage />} />
                <Route path="/test-runs" element={<AdminTestRunsPage />} />
                <Route path="/email-management" element={<AdminEmailManagementPage />} />
                <Route path="/memory" element={<AdminMemoryBrowserPage />} />
                <Route path="/memory/:group" element={<AdminMemoryBrowserPage />} />
                <Route path="/memory/:group/:key" element={<AdminMemoryBrowserPage />} />
                <Route path="/knowledge" element={<AdminKnowledgePage />} />
                <Route path="/environment" element={<AdminEnvironmentPage />} />
                <Route path="/models" element={<AdminModelsPage />} />
                <Route path="/cluster" element={<AdminClusterPage />} />
                <Route path="/jobs" element={<AdminBackgroundJobsPage />} />
                <Route path="/profiling" element={<AdminProfilingPage />} />
                <Route path="/ticket-management" element={<AdminTicketManagementPage />} />
                <Route path="/data-export" element={<AdminDataExportPage />} />
              </Routes>
            </AdminLayout>
          } />
        )}

        {/* All other routes - WITH Layout wrapper */}
        <Route path="*" element={
          <Layout>
            <Routes>
              <Route path="/access-denied" element={<AccessDeniedPage />} />
              <Route path="/" element={<TicketViewer />} />
              <Route path="/tickets" element={<TicketViewer />} />
              <Route path="/tickets/:ticketId" element={<TicketViewer />} />
              <Route path="/analytics" element={<AnalyticsDashboard />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Layout>
        } />
      </Routes>
      <ScreenSaver />

      {/* Global Access Denied Modal */}
      <Dialog 
        open={accessDeniedModal.open}
        fullScreen
        disableEscapeKeyDown
      >
        <Box
          sx={{
            minHeight: '100vh',
            width: '100vw',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            p: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
            <Box
              sx={{
                p: 4,
                textAlign: 'center'
              }}
            >
              {/* AGENTFLOW Header */}
              <Box sx={{ mb: 4 }}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    color: '#b0b0b0',
                    fontWeight: 300,
                    fontSize: '3.5rem',
                    letterSpacing: '0.2em',
                    textShadow: '0 0 20px rgba(176, 176, 176, 0.3)',
                  }}
                >
                  AGENTFLOW
                </Typography>
              </Box>

              {/* Message */}
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  color: '#b0b0b0', 
                  fontWeight: 400,
                  maxWidth: 600,
                  mx: 'auto'
                }}
              >
                You don't have permission to view this page just yet. If that feels wrong, your administrator can set things straight.
              </Typography>

              {user && (
                <Box sx={{ 
                  mt: 4, 
                  p: 3, 
                  bgcolor: 'rgba(176, 176, 176, 0.1)', 
                  borderRadius: 2,
                  maxWidth: 500,
                  mx: 'auto',
                  border: '1px solid rgba(176, 176, 176, 0.3)'
                }}>
                  <Typography variant="body1" sx={{ mb: 2, color: '#b0b0b0' }}>
                    <strong>You're signed in as:</strong>
                  </Typography>
                  <Typography variant="h6" sx={{ mb: 2, color: '#e0e0e0' }}>
                    {user.email || user.name || 'Unknown User'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(176, 176, 176, 0.8)' }}>
                    Contact your administrator if you need access to this application.
                  </Typography>
                </Box>
              )}

              {/* Logout Link */}
              <Box sx={{ mt: 4 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleLogout}
                  sx={{
                    color: 'rgba(176, 176, 176, 0.8)',
                    textDecoration: 'underline',
                    '&:hover': {
                      color: '#b0b0b0',
                    },
                  }}
                >
                  Sign out and try a different account
                </Link>
              </Box>
            </Box>
          </Container>
        </Box>
      </Dialog>
    </ScreenSaverProvider>
  )
}

export default App